// The brand portal's pages (docs/ops/brand-portal.md). On the small card pages
// (log in, an invite) it posts the page's view once it loads, so link scanners
// that only fetch the page count nothing. On a brand's page it sends Find a fix
// requests, switches the nav tabs without a reload (they are plain links, so the
// address keeps the tab), opens rows, and posts each press, then redraws the
// row from the server's answer. A team preview posts too: the
// server records it as test and saves nothing. PostHog loads only after the
// consent banner and gets the same stage names with plain props.
import './sentry.ts';
import { initConsent, track } from './consent.ts';
import { $, $$, pageData } from './dom.ts';

interface PortalData {
  portal?: { page: 'card' | 'brand'; seen?: string; brand?: string; preview?: boolean; arrived?: string | null; tab?: string };
}

const data = pageData<PortalData>().portal;

const post = (url: string, body: unknown = {}, keepalive = false) =>
  fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), credentials: 'same-origin', keepalive });

if (data?.page === 'card' && data.seen) post(data.seen, {}, true).catch(() => {});
if (data?.page === 'brand' && data.brand) brandPage(data.brand, data.preview === true, data.arrived ?? null, data.tab ?? 'all');

function brandPage(brand: string, preview: boolean, arrived: string | null, initialTab: string): void {
  const STATUSES = ['waiting', 'in_progress', 'complete', 'working_well'] as const;
  type Status = (typeof STATUSES)[number];
  const LABEL: Record<Status, string> = { waiting: '', in_progress: 'In progress', complete: 'Complete', working_well: 'Working well' };
  const base = `/app/${brand}`;

  // PostHog after consent: the page view, and a sign-in that just happened (once, via a cookie the server spent).
  const viewed = () => {
    void track('pt_dashboard_viewed', { brand });
    if (arrived) void track(arrived, { brand });
  };
  if (!preview) {
    initConsent((c) => {
      if (c.choice === 'accept' && c.mode !== 'accepted') setTimeout(viewed, 0);
    });
    viewed();
  }
  const capture = (event: string, props: Record<string, string | number | boolean>) => {
    if (!preview) void track(event, { brand, ...props });
  };

  // Tabs: links that the script switches in place. A row the person just pressed stays shown (pinned) until the tab
  // changes, so a press on Waiting on you doesn't make the row vanish under their finger.
  const tabs = $$<HTMLAnchorElement>('[data-tab]');
  const items = $$<HTMLLIElement>('.pt-item');
  const shown = $('[data-shown]');
  const pinned = new Set<HTMLLIElement>();
  let tab = initialTab;

  function apply(announce: boolean): void {
    let visible = 0;
    for (const a of tabs) {
      if (a.dataset.tab === tab) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    }
    for (const li of items) {
      const hide = tab !== 'all' && li.dataset.status !== tab && !pinned.has(li);
      li.hidden = hide;
      if (!hide) visible++;
    }
    for (const p of $$<HTMLElement>('[data-empty]')) p.hidden = !(p.dataset.empty === tab && visible === 0);
    if (announce && shown) {
      const label = tabs.find((a) => a.dataset.tab === tab)?.firstChild?.textContent?.trim() ?? '';
      shown.textContent = `Showing ${label}, ${visible} ${visible === 1 ? 'item' : 'items'}`;
    }
  }
  function counts(): void {
    for (const s of STATUSES) {
      const el = $(`[data-count="${s}"]`);
      if (el) el.textContent = String(items.filter((li) => li.dataset.status === s).length);
    }
  }
  function reveal(): void {
    tabs.find((a) => a.dataset.tab === tab)?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }
  function choose(next: string, push: boolean): void {
    tab = next;
    pinned.clear();
    apply(true);
    if (push) history.pushState({ tab }, '', next === 'all' ? base : `${base}?tab=${next}`);
  }
  for (const a of tabs) {
    a.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      const next = a.dataset.tab!;
      if (next === tab) return;
      choose(next, true);
      post(`${base}/tab`, { tab: next }).catch(() => {});
      capture('pt_tab_changed', { tab: next });
    });
  }
  window.addEventListener('popstate', () => {
    // Only a tab the page rendered: an unknown ?tab= would hide every row, as the server's tabOf() refuses it too.
    const asked = new URLSearchParams(location.search).get('tab');
    choose(asked && tabs.some((a) => a.dataset.tab === asked) ? asked : 'all', false);
    reveal();
  });
  reveal();

  // Find a fix: a counter near the limit (no maxlength, which would cut pasted text silently), the text kept when a
  // send fails, and on success the form swaps for the success card until Send another brings it back.
  const askForm = $<HTMLFormElement>('[data-ask]');
  const sent = $<HTMLElement>('[data-ask-sent]');
  if (askForm && sent) {
    const box = askForm.querySelector<HTMLTextAreaElement>('textarea')!;
    const btn = askForm.querySelector<HTMLButtonElement>('button[type=submit]')!;
    const count = askForm.querySelector<HTMLElement>('[data-ask-count]')!;
    const state = askForm.querySelector<HTMLElement>('[data-ask-state]')!;
    const max = Number(askForm.dataset.max ?? '2000');
    let sending = false;
    const sayAsk = (text: string, bad = false) => {
      state.textContent = text;
      state.classList.toggle('pt-ask-bad', bad);
    };
    const measure = () => {
      const n = box.value.trim().length;
      const over = n > max;
      count.hidden = n <= max - 200;
      count.textContent = over ? `${n - max} over the ${max.toLocaleString('en-GB')} limit` : `${max - n} left`;
      count.classList.toggle('pt-ask-bad', over);
      btn.disabled = sending || over;
    };
    box.addEventListener('input', () => {
      if (state.textContent) sayAsk('');
      measure();
    });
    box.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        askForm.requestSubmit();
      }
    });
    askForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (sending) return;
      const text = box.value.trim();
      if (!text) {
        sayAsk('Tell us what you want fixed first.', true);
        box.focus();
        return;
      }
      if (text.length > max) return measure();
      sending = true;
      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = 'Sending...';
      let res: Response | null = null;
      try {
        res = await post(`${base}/ask`, { text });
      } catch {
        res = null;
      }
      sending = false;
      btn.textContent = label;
      if (res?.status === 401) {
        location.href = '/login';
        return;
      }
      const json = res && res.headers.get('content-type')?.includes('application/json') ? ((await res.json().catch(() => ({}))) as Record<string, unknown>) : {};
      if (res?.ok) {
        box.value = '';
        sayAsk('');
        measure();
        sent.querySelector<HTMLElement>('[data-ask-quote]')!.textContent = text;
        askForm.hidden = true;
        sent.hidden = false;
        sent.focus();
        capture('pt_fix_asked', { chars: text.length, slack: json.slack === true });
      } else if (res?.status === 400) {
        sayAsk(json.error === 'too_long' ? `That's over ${max.toLocaleString('en-GB')} characters. Shorten it and try again.` : 'Tell us what you want fixed first.', true);
        measure();
      } else {
        sayAsk("That didn't send. Your text is still here, please try again.", true);
        measure();
      }
    });
    sent.querySelector('[data-ask-again]')!.addEventListener('click', () => {
      sent.hidden = true;
      askForm.hidden = false;
      box.focus();
      post(`${base}/ask/again`).catch(() => {});
      capture('pt_fix_again', {});
    });
  }

  // Rows open and close, one at a time, so only one action bar sticks to the screen; the first open of each per page
  // load is counted.
  const opened = new Set<string>();
  function setOpen(li: HTMLLIElement, open: boolean): void {
    const toggle = li.querySelector<HTMLButtonElement>('.pt-toggle')!;
    if (open) {
      for (const other of items) if (other !== li && other.hasAttribute('data-open')) setOpen(other, false);
      // A long card closing above can leave the tapped card's header off the top of the screen.
      if (toggle.getBoundingClientRect().top < 0) toggle.scrollIntoView({ block: 'start' });
    }
    const detail = li.querySelector<HTMLElement>('.pt-detail')!;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    detail.hidden = !open;
    li.toggleAttribute('data-open', open);
    const slug = li.dataset.slug!;
    if (open && !opened.has(slug)) {
      opened.add(slug);
      post(`${base}/insights/${slug}/opened`).catch(() => {});
      capture('pt_insight_opened', { insight: slug });
    }
  }
  for (const li of items) li.querySelector('.pt-toggle')!.addEventListener('click', () => setOpen(li, !li.hasAttribute('data-open')));

  apply(false);
  const first = items.find((li) => !li.hidden);
  if (first) setOpen(first, true);

  const say = (li: HTMLLIElement, text: string) => {
    const el = li.querySelector<HTMLElement>('.pt-state');
    if (el) el.textContent = text;
  };
  const FAILED = "That didn't go through. Try again.";
  const PREVIEW = 'Preview: this is what they would see. Nothing was saved or sent.';

  /** Posts a press; a lapsed session goes to /login. Null when it failed. */
  async function press(slug: string, action: string, body: unknown = {}): Promise<{ status: number; json: Record<string, unknown> } | null> {
    try {
      const res = await post(`${base}/insights/${slug}/${action}`, body);
      if (res.status === 401) {
        location.href = '/login';
        return null;
      }
      const json = res.headers.get('content-type')?.includes('application/json') ? ((await res.json()) as Record<string, unknown>) : {};
      return res.ok || res.status === 409 ? { status: res.status, json } : null;
    } catch {
      return null;
    }
  }

  /** `pin` keeps the row shown on the tab the press was made from; false when the person changed tab meanwhile. */
  function setStatus(li: HTMLLIElement, status: Status, pin: boolean): void {
    li.dataset.status = status;
    const check = li.querySelector('.pt-check');
    if (check) check.className = `pt-check pt-check-${status}`;
    const label = li.querySelector('[data-status-label]');
    if (label) label.textContent = LABEL[status];
    const change = li.querySelector<HTMLButtonElement>('[data-change]');
    if (change && status !== 'waiting') {
      const note = document.createElement('p');
      note.className = status === 'in_progress' ? 'pt-note pt-note-progress' : 'pt-note pt-note-done';
      note.textContent = status === 'in_progress' ? "We're on it. We'll tell you when it's live." : 'Done.';
      note.tabIndex = -1;
      change.replaceWith(note);
      note.focus();
    }
    if (pin) pinned.add(li);
    counts();
    apply(false);
  }

  for (const li of items) {
    const slug = li.dataset.slug!;
    const form = li.querySelector<HTMLFormElement>('.pt-fb');
    const opener = li.querySelector<HTMLButtonElement>('[data-feedback-open]');
    const openForm = (open: boolean) => {
      if (!form) return;
      form.hidden = !open;
      opener?.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) form.querySelector<HTMLInputElement>('input[name=reason]')?.focus();
    };

    li.querySelector<HTMLButtonElement>('[data-change]')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = 'Sending...';
      const pressedOn = tab;
      const out = await press(slug, 'change');
      if (!out) {
        btn.disabled = false;
        btn.textContent = label;
        say(li, FAILED);
        return;
      }
      const status = String(out.json.status ?? 'in_progress') as Status;
      setStatus(li, status, tab === pressedOn);
      say(li, out.status === 409 ? 'Already done.' : preview ? PREVIEW : "Thanks. We've told the team.");
      if (out.status !== 409) capture('pt_change_requested', { insight: slug });
    });

    opener?.addEventListener('click', () => openForm(form ? form.hidden !== false : false));
    form?.querySelector('[data-feedback-close]')?.addEventListener('click', () => openForm(false));
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const reason = String(fd.get('reason') ?? '');
      const note = String(fd.get('note') ?? '').trim();
      if (!reason && !note) {
        say(li, 'Pick a reason or add a note.');
        return;
      }
      const btn = form.querySelector<HTMLButtonElement>('button[type=submit]')!;
      btn.disabled = true;
      const out = await press(slug, 'feedback', { reason: reason || null, note: note || null });
      btn.disabled = false;
      if (!out) return say(li, FAILED);
      openForm(false);
      form.reset();
      say(li, preview ? PREVIEW : "Thanks, we've told the team.");
      if (!preview) {
        let told = li.querySelector<HTMLElement>('.pt-told');
        if (!told) {
          told = document.createElement('p');
          told.className = 'pt-told';
          li.querySelector('.pt-detail')!.prepend(told);
        }
        told.textContent = `You told us: ${[reason, note ? `"${note}"` : ''].filter(Boolean).join('. ')}`;
      }
      capture('pt_feedback_sent', { insight: slug, reason: reason || 'none' });
    });

    for (const thumb of li.querySelectorAll<HTMLButtonElement>('[data-rate]')) {
      thumb.addEventListener('click', async () => {
        const value = Number(thumb.dataset.rate);
        const out = await press(slug, 'rate', { value });
        if (!out) return say(li, FAILED);
        for (const t of li.querySelectorAll<HTMLButtonElement>('[data-rate]')) t.setAttribute('aria-pressed', t === thumb ? 'true' : 'false');
        capture('pt_insight_rated', { insight: slug, value });
        // A thumbs-down asks what's wrong, so the rating changes what the person sees next.
        if (value === -1) openForm(true);
      });
    }

    li.querySelector<HTMLAnchorElement>('[data-call]')?.addEventListener('click', () => {
      post(`${base}/insights/${slug}/call`, {}, true).catch(() => {});
      capture('pt_call_opened', { insight: slug });
    });
  }
}
